import React, { useRef } from 'react';
import _merge from 'lodash/merge';
import { RADIUS_2 } from '@nokia-csf-uxr/freeform-design-tokens/tokens/border-radius';

import { getContext, useCombinedRefs } from 'common';
import Elevation, { ElevationProps } from 'components/Elevation';

import styled from './styles';

const ELEVATION_INDEX = 1;
const ELEVATION_DARK = false;
const ELEVATION_STYLE = {
  display: 'flex',
  flexDirection: 'column' as const,
  maxWidth: 'fit-content',
  width: '-moz-fit-content',
  cursor: 'pointer'
};

export interface ToggleButtonGroupProps extends React.ComponentPropsWithRef<'div'> {
  /** The content of ToggleButtonGroupProps, consider using ToggleGroupButton */
  children?: React.ReactNode;
  /** To override the ToggleButtonGroup theme please refer to "Readme" tab to learn more. */
  theme?: any;
  /** Override the `Elevation` properties. See `Elevation` component for details. */
  elevationProps?: Partial<ElevationProps>;
}

/** Uncontrolled ToggleButtonGroup.
 * All additional props will be forwarded to the root element.
 * To get the ref of the outermost element use `ref` prop. [Read React useRef hook documentation](https://reactjs.org/docs/hooks-reference.
 */
const ToggleButtonGroup = React.forwardRef<HTMLElement, ToggleButtonGroupProps>((props: ToggleButtonGroupProps, ref) => getContext(({ RTL, theme }) => {
  const {
    children,
    theme: themeProp,
    elevationProps,
    ...otherProps
  } = props;
  const { style: elevationStyle, ...otherElevationProps } = elevationProps;

  const resolvedTheme = _merge({}, theme.TOGGLE_BUTTON_GROUP, themeProp);

  const groupRef = useRef();
  const combinedRef = useCombinedRefs(ref, groupRef);
  return (
    <Elevation
      elevationIndex={ELEVATION_INDEX}
      dark={ELEVATION_DARK}
      rtl={RTL}
      style={{
        ...ELEVATION_STYLE,
        borderRadius: RADIUS_2,
        ...elevationStyle
      }}
      {...otherElevationProps}
    >
      <styled.GroupWrapper RTL={RTL} ref={combinedRef} theme={resolvedTheme} {...otherProps}>
        {children}
      </styled.GroupWrapper>
    </Elevation>
  );
}));

ToggleButtonGroup.defaultProps = {
  children: undefined,
  theme: {},
  elevationProps: {},
};

ToggleButtonGroup.displayName = 'ToggleButtonGroup';

export default ToggleButtonGroup;
